import { STAGE_NAME } from '@eigtech/ui-shared-environment-web'
import { OwnerUiTheme } from '@eigtech/white-label-types'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { ColorScale, generateColorScale, resolveForeground } from './colorScale'
import { toTinyColor } from './normalizeColor'

/**
 * Chakra color scales derived from the owner's theme. `brandPrimary` and
 * `brandSecondary` override the hardcoded scales in `@eigtech/ui-shared-dave`;
 * the other two are additive.
 */
export type WhiteLabelChakraColors = Partial<{
  brandAccent: ColorScale
  brandMuted: ColorScale
  brandPrimary: ColorScale
  brandSecondary: ColorScale
}>

/** A local patch layered over the theme the white label service serves. */
export type OwnerUiThemeOverride = Partial<OwnerUiTheme>

type WhiteLabelThemeState = {
  chakraColors: WhiteLabelChakraColors | undefined
  /** The theme as served by the white label service, before any local override. */
  serviceTheme: OwnerUiTheme | undefined
  /** Fields overridden locally, which win over the served theme. */
  themeOverride: OwnerUiThemeOverride
  /** A theme standing in for the served one while it is being edited. */
  themePreview: OwnerUiTheme | undefined
}

type PersistedWhiteLabelThemeState = Pick<WhiteLabelThemeState, 'themeOverride'>

/**
 * The theme is applied from a route loader, above every provider in the tree, so it
 * is held in a module store rather than in context - `DaveProvider` sits above
 * `QueryClientProvider` in every app and cannot read the query cache.
 *
 * Only the local override is persisted; the served theme and the scales derived from
 * it are refetched on every load.
 */
const useWhiteLabelThemeStore = create<WhiteLabelThemeState>()(
  persist<WhiteLabelThemeState, [], [], PersistedWhiteLabelThemeState>(
    () => ({
      chakraColors: undefined,
      serviceTheme: undefined,
      themeOverride: {},
      themePreview: undefined,
    }),
    {
      name: `eig.white-label.theme-override.${STAGE_NAME}`,
      partialize: ({ themeOverride }) => ({ themeOverride }),
    }
  )
)

export const useWhiteLabelChakraColors = () =>
  useWhiteLabelThemeStore((state) => state.chakraColors)

/** The theme as served by the white label service, before any local override. */
export const useWhiteLabelServiceTheme = () =>
  useWhiteLabelThemeStore((state) => state.serviceTheme)

/** The fields currently overridden locally. */
export const useWhiteLabelThemeOverride = () =>
  useWhiteLabelThemeStore((state) => state.themeOverride)

/**
 * Tailwind/shadcn tokens in `@eigtech/config-tailwind/theme.css` that the owner's
 * UI theme maps onto.
 */
const cssVariables = (theme: Partial<OwnerUiTheme>) => ({
  '--accent': theme.accentColor,
  '--accent-foreground': resolveForeground(theme.accentColor, theme.accentForegroundColor),
  '--muted': theme.mutedColor,
  '--muted-foreground': resolveForeground(theme.mutedColor, theme.mutedForegroundColor),
  '--primary': theme.primaryColor,
  '--primary-foreground': resolveForeground(theme.primaryColor, theme.primaryForegroundColor),
  '--secondary': theme.secondaryColor,
  '--secondary-foreground': resolveForeground(theme.secondaryColor, theme.secondaryForegroundColor),
  '--border': theme.borderColor,
  '--input': theme.inputColor,
  '--ring': theme.ringColor,
  '--sidebar': theme.sidebarColor,
  '--sidebar-foreground': resolveForeground(theme.sidebarColor, theme.sidebarForegroundColor),
  '--sidebar-primary': theme.sidebarPrimaryColor,
  '--sidebar-primary-foreground': resolveForeground(
    theme.sidebarPrimaryColor,
    theme.sidebarPrimaryForegroundColor
  ),
  '--sidebar-accent': theme.sidebarAccentColor,
  '--sidebar-accent-foreground': resolveForeground(
    theme.sidebarAccentColor,
    theme.sidebarAccentForegroundColor
  ),
  '--sidebar-border': theme.sidebarBorderColor,
  '--sidebar-ring': theme.sidebarRingColor ?? theme.sidebarPrimaryColor,
})

/**
 * Apply an owner's UI theme (`OwnerTheme['ui']` - the email theme is a separate
 * config, consumed by `@eigtech/mjml-templates`) to both theming systems: the
 * Tailwind custom properties on `:root`, and the chakra scales consumed via
 * {@link useWhiteLabelChakraColors}.
 *
 * Any local override set through {@link setWhiteLabelThemeOverride} is layered on
 * top. Call this before the app shell renders; call it with no theme when the
 * service could not be reached, so a persisted override still applies.
 */
export function applyWhiteLabelTheme(theme?: OwnerUiTheme) {
  if (theme) useWhiteLabelThemeStore.setState({ serviceTheme: theme })

  applyEffectiveTheme()
}

/**
 * Overwrite fields of the served theme locally. The override is persisted to
 * localStorage and re-layered on every load, so it wins over whatever the white
 * label service returns until it is cleared.
 */
export function setWhiteLabelThemeOverride(themeOverride: OwnerUiThemeOverride) {
  useWhiteLabelThemeStore.setState({ themeOverride })

  applyEffectiveTheme()
}

/** Drop the local override and fall back to the served theme. */
export const clearWhiteLabelThemeOverride = () => setWhiteLabelThemeOverride({})

/**
 * Paint a theme in place of the one the service serves, so an owner's colors can be
 * judged on the running app while they are being edited. Unlike the override this is
 * not persisted - it lasts until it is cleared or the page is reloaded, and it is the
 * caller's job to clear it once the editor is gone.
 *
 * A local override still layers on top, exactly as it would once the theme is saved.
 */
export function setWhiteLabelThemePreview(themePreview: OwnerUiTheme | undefined) {
  useWhiteLabelThemeStore.setState({ themePreview })

  applyEffectiveTheme()
}

/** Stop previewing and go back to the served theme. */
export const clearWhiteLabelThemePreview = () => setWhiteLabelThemePreview(undefined)

/**
 * Paint the served theme - or the one being previewed in its place - with the local
 * override layered over it. Colors that fail to parse, or that none of the three sets,
 * clear their custom property, so the defaults in `theme.css` and
 * `@eigtech/ui-shared-dave` come back.
 */
function applyEffectiveTheme() {
  const { serviceTheme, themeOverride, themePreview } = useWhiteLabelThemeStore.getState()
  const theme = mergeThemeOverride(themePreview ?? serviceTheme, themeOverride)

  const { style } = document.documentElement

  Object.entries(cssVariables(theme)).forEach(([variable, color]) => {
    const value = toCssColor(color)

    if (value) style.setProperty(variable, value)
    else style.removeProperty(variable)
  })

  useWhiteLabelThemeStore.setState({
    chakraColors: omitUndefined({
      brandAccent: generateColorScale(theme.accentColor),
      brandMuted: generateColorScale(theme.mutedColor),
      brandPrimary: generateColorScale(theme.primaryColor),
      brandSecondary: generateColorScale(theme.secondaryColor),
    }),
  })
}

/** Blank override fields mean "keep the served value", so they are dropped. */
const mergeThemeOverride = (
  theme: OwnerUiTheme | undefined,
  override: OwnerUiThemeOverride
): Partial<OwnerUiTheme> => ({
  ...theme,
  ...Object.fromEntries(Object.entries(override).filter(([, value]) => !!value)),
})

const toCssColor = (color: string | undefined) => toTinyColor(color)?.toHexString()

const omitUndefined = (colors: WhiteLabelChakraColors) =>
  Object.fromEntries(
    Object.entries(colors).filter(([, scale]) => scale !== undefined)
  ) as WhiteLabelChakraColors
